import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { isBreakpointLargeOrAbove, useBreakpoint } from '@noths/polaris-client-ribbons-base';

import { adaptStoreToUIFacetHrefs } from 'src/adapters/storeToPageURL/adaptStoreToUIFacetHrefs';
import { adaptFacetToUIFacet } from 'src/adapters/storeToUI/adaptFacetToUIFacet';
import { DynamicFacetPills } from 'src/components/organisms/DynamicFacetPills/DynamicFacetPills';
import { FacetDrawer } from 'src/components/organisms/FacetDrawer/FacetDrawer';
import { getViewResultsButtonText } from 'src/components/organisms/ListingsControlsDrawer/getViewResultsButtonText';
import type { ReduxApplicationState } from 'src/redux/combinedReducer';
import { selectActiveFacets } from 'src/redux/productsQuery/selectors/selectors';
import { actions } from 'src/redux/productsQuery/slice';
import {
  trackFacetPillClose,
  trackFacetPillImpression,
  trackFacetPillOpen,
  trackFacetPillScroll,
  trackFacetPillViewResults,
} from 'src/tracking/events/facetPillInteraction';
import { isCategoryPage, isPartnerPage, isSearchPage, isTaxonomyPage } from 'src/utils/page';

export const DynamicFacetPillsContainer = () => {
  const state = useSelector((s: ReduxApplicationState) => s);
  const dispatch = useDispatch();
  const breakpoint = useBreakpoint();
  const { openPillFacetKey } = state.productsQuery;
  // Local, unlike the facet key: this only exists to drive the close animation,
  // and nothing outside the pill row needs to know about it.
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);

  const { pageType } = state.navigation;
  // Promoted facets pill on every listing page; dynamic ones only where there is a
  // broad result set to narrow. Listed explicitly so a new page type is a decision.
  const isEnabled =
    isCategoryPage(pageType) ||
    isPartnerPage(pageType) ||
    isSearchPage(pageType) ||
    isTaxonomyPage(pageType);
  const showsDynamicPills = isSearchPage(pageType) || isTaxonomyPage(pageType);

  const pillFacets = useMemo(() => {
    const uiFacets = adaptFacetToUIFacet(
      state.productsQuery.facets,
      state.productsQuery.taxonomyAttributeAliases,
      { keepEmptyPromotedFacets: true },
    );

    // `promoted` is merchandising intent, `static` is panel position: separate facts.
    // It only ever adds, so on a dynamic facet it does nothing.
    const selected = adaptStoreToUIFacetHrefs(state, uiFacets).filter(
      (facet) => facet.promoted || (showsDynamicPills && !facet.static),
    );

    // Promoted pills are pinned to the front. Store order cannot be trusted for that,
    // as `adaptResponseFacetsToStore` reorders by response order. Stable partition, so
    // config order survives within each half.
    return [
      ...selected.filter((facet) => facet.promoted),
      ...selected.filter((facet) => !facet.promoted),
    ];
    // eslint-disable-next-line react-hooks/exhaustive-deps -- `state` is used in the callback; facet hrefs only change when query, path, or hash change
  }, [
    state.productsQuery,
    state.router.location.pathname,
    state.navigation.hash,
    showsDynamicPills,
  ]);

  // Kept live from the store by key (not snapshotted) so option counts update as
  // the user selects; the key is only cleared once the drawer has finished
  // closing (`onAfterClose`), which keeps the facet content in place during the
  // close animation.
  const openFacet = openPillFacetKey
    ? pillFacets.find((facet) => facet.key === openPillFacetKey) ?? null
    : null;

  // If the open facet drops out of the store while the drawer is open (e.g. a
  // fresh response no longer returns it), `openFacet` becomes null and the
  // drawer would render empty with no close control. Close it so the user isn't
  // stranded; `onAfterClose` then clears the key. This only fires in that
  // anomalous state — during a normal close `isDrawerOpen` is already false.
  useEffect(() => {
    if (isDrawerOpen && openPillFacetKey && !openFacet) {
      setIsDrawerOpen(false);
    }
  }, [isDrawerOpen, openPillFacetKey, openFacet]);

  // The pill row is hidden in CSS from L up, where the sidebar takes over filtering. The
  // drawer is a portal outside that subtree, so nothing else would close it on a resize
  // across the breakpoint, leaving it open with no pill to reopen it. Keep in step with
  // `listingsLayoutFacetPills`.
  useEffect(() => {
    if (isBreakpointLargeOrAbove(breakpoint)) {
      setIsDrawerOpen(false);
    }
  }, [breakpoint]);

  const getFiltersApplied = () =>
    selectActiveFacets(state)
      .map((facet) => facet.title)
      .join(', ');

  const handleImpression = () => {
    trackFacetPillImpression({
      facetTitles: pillFacets.map((facet) => facet.title),
      promotedCount: pillFacets.filter((facet) => facet.promoted).length,
    });
  };

  const handlePillClick = (facetKey: string) => {
    trackFacetPillOpen({
      facetKey,
      facetTitle: pillFacets.find((facet) => facet.key === facetKey)?.title ?? '',
      filtersApplied: getFiltersApplied(),
      index: pillFacets.findIndex((facet) => facet.key === facetKey),
    });
    dispatch(actions.openPillFacet(facetKey));
    setIsDrawerOpen(true);
  };

  const handleViewResults = () => {
    if (openFacet) {
      trackFacetPillViewResults({
        facetTitle: openFacet.title,
        filtersApplied: getFiltersApplied(),
      });
    }

    setIsDrawerOpen(false);
  };

  // Only a dismissal by the customer. The drawer is also closed from the effects
  // above, which is the app tidying up rather than anything to report.
  const handleDismiss = () => {
    if (openFacet) {
      trackFacetPillClose({ facetTitle: openFacet.title });
    }

    setIsDrawerOpen(false);
  };

  if (!isEnabled) {
    return null;
  }

  return (
    <>
      <DynamicFacetPills
        facets={pillFacets}
        onFirstScroll={trackFacetPillScroll}
        onImpression={handleImpression}
        onPillClick={handlePillClick}
        openFacetKey={isDrawerOpen ? openPillFacetKey : null}
      />
      <FacetDrawer
        ctaText={getViewResultsButtonText(state.products)}
        facet={openFacet}
        isOpen={isDrawerOpen}
        loading={state.products.pageLoading !== null}
        onAfterClose={() => dispatch(actions.closePillFacet())}
        onClose={handleDismiss}
        onViewResults={handleViewResults}
      />
    </>
  );
};
